
import AreaChart from "@/components/chart/AreaChart";
import HeartIcon from "@/components/marketAnalysis/icons/HeartIcon";
import HeartIconFilled from "@/components/marketAnalysis/icons/HeartIconFilled";
import PercentBox from '@/components/shared/PercentBox';
import AddTransactionToPosition from "@/components/transaction/AddTransactionToPosition";
import { backendExceptionHandler, companyLogoHandler } from '@/mixins';
import FinAgentService from '@/service/FinAgentService';
import FormatterService from "@/service/FormatterService";

export default {
    name: 'FinAgentStockMenuCard',
    components: {
        PercentBox,
        AreaChart,
        AddTransactionToPosition,
        HeartIcon,
        HeartIconFilled,
    },
    
    mixins: [ companyLogoHandler, backendExceptionHandler ],

    props: {
        stockParams: {
            type: Object,
            default: () => {}
        },
    },

    data() {
        return {
            stockData: {},
            isLoading: false,
            isAddToPortfolioMenuOpen: false,
            portfolios: [],
            interval: this.$store.getters["preferences/getFinAgentStockChartInterval"],
            errorMessage: '',
        };
    },

    computed: {
        portfolioIcon() {
            return this.$vuetify.theme.dark ?
                require('@/assets/icons/add-to-portfolio-dark.svg') :
                require('@/assets/icons/add-to-portfolio-light.svg');
        },
        virtualPortfolioIcon() {
            return this.$vuetify.theme.dark ?
                require('@/assets/icons/virtual-icon-dark.svg') :
                require('@/assets/icons/virtual-icon-light.svg');
        },
        disabledSeeDetails() {
            return this.isLoading || !this.stockData.slug;
        },
        intervals() {
            return [
                {
                    interval: 'day',
                    text: this.$t('component.share.sharePage.day')
                },
                {
                    interval: 'week',
                    text: this.$t('common.intervals.week')
                },
                {
                    interval: 'month',
                    text: this.$t('common.intervals.month')
                },
                {
                    interval: '3m',
                    text: this.$t('common.intervals.threeMonths')
                },
                {
                    interval: '6m',
                    text: this.$t('common.intervals.sixMonths')
                },
                {
                    interval: 'ytd',
                    text: this.$t('common.intervals.ytd')
                },
                {
                    interval: 'year',
                    text: this.$t('common.intervals.year')
                },
                {
                    interval: '2y',
                    text: this.$t('common.intervals.twoYears')
                },
                {
                    interval: '3y',
                    text: this.$t('common.intervals.threeYears')
                },
                {
                    interval: '4y',
                    text: this.$t('common.intervals.fourYears')
                },
                {
                    interval: 'fiveyears',
                    text: this.$t('common.intervals.fiveYears')
                },
                {
                    interval: '10y',
                    text: this.$t('common.intervals.tenYears')
                },
                {
                    interval: '20y',
                    text: this.$t('common.intervals.twentyYears')
                },
                {
                    interval: 'max',
                    text: this.$t('common.intervals.max')
                }
            ]
        },
        isLoggedIn() {
            return !!this.$store.state.account.user
        },
        isWishListItem() {
            return this.$store.state.finAgent.wishListIsins.includes(this.stockData.isin);
        },
    },

    watch: {
        stockParams: {
            immediate: true,
            handler(params) {
                this.cancelActiveRequests()
                if (params && params.isin) {
                    this.stockData.isin = params.isin
                    this.stockData.companyLogo = '';
                    this.fetchQuoteData(params.isin)
                }
            }
        }
    },

    created() {
        this.format = new FormatterService()
        this.finAgentService = new FinAgentService(this.$axios)
    },

    beforeDestroy() {
        this.cancelActiveRequests()
    },

    methods: {
        cancelActiveRequests() {
            if (this.activeQuoteRequest) {
                this.activeQuoteRequest.cancel()
            }
            if (this.activePortfolioRequest) {
                this.activePortfolioRequest.cancel()
            }
        },
        async fetchQuoteData(isin = '') {
            try {
                this.isLoading = true;
                this.cancelActiveRequests();
                const url = '/api/shares/details';
                const params = { isin };
                const axiosSource = this.$axios.CancelToken.source();
                const cancelToken = axiosSource.token;
                this.activeQuoteRequest = { cancel: axiosSource.cancel, msg: "Cancel loading stock data..." };
                const res = await this.$axios.get(url, { params, cancelToken });
                
                if (res.data) {
                    this.stockData = { ...res.data };
                }

                this.activeQuoteRequest = null;
            } catch (_) {
                this.activeQuoteRequest = null;
            } finally {
                this.isLoading = false;
            }
        },
        queryPortfolios(isVirtual) {
            this.portfolios = [];
            const url = '/api/portfolio_tabs';
            const params = {
                context: isVirtual ? 'virtual' : 'default'
            }
            const axiosSource = this.$axios.CancelToken.source();
            const cancelToken = axiosSource.token;
            this.activePortfolioRequest = { cancel: axiosSource.cancel, msg: "Cancel query portfolios..." };
            this.$axios
                .get(url, { params, cancelToken })
                .then(response => {
                    this.portfolios = response.data.filter((p) => p.portfolioType !== "linked" && !p.live);
                    this.activePortfolioRequest = null;
                })
                .catch(() => {
                    this.portfolios = [];
                    this.activePortfolioRequest = null;
                })
        },
        handleClickAddToPortfolio(isVirtual) {
            if (!this.stockData.slug) {
                return;
            }
            this.queryPortfolios(isVirtual);
            this.openAddTransactionDialog();
        },
        getStockDetailsLink() {            
            if (this.disabledSeeDetails) {
                return '';
            }
            return { name : this.$getTranslatedRouteName('aktie-share'), params: { share: this.stockData.slug }};
        },
        openAddTransactionDialog(type = null) {
            const transactionData = JSON.parse(JSON.stringify(this.stockData));
            transactionData.logo = transactionData.companyLogo;
            transactionData.instrumentType = transactionData.type;
            transactionData.type = type;
            transactionData.qty = 0;
            transactionData.id = null;
            this.$root.$emit('request-transaction-window', transactionData, 0, null, false, 'stock-menu-card-add-transaction');
        },
        handleAddedSuccess() {
            this.$store.commit('snackbar/updateType', 'success');
            this.$store.commit('snackbar/updateMessage', this.$t('component.share.sharePage.positionAdded'));
        },
        async handleClickAddWishList() {
            try {
                await this.finAgentService.addToWatchList(this.stockData.isin);
                this.$store.commit('finAgent/addWishListIsin', this.stockData.isin);
                this.$store.commit('snackbar/updateType', 'success');
                this.$store.commit('snackbar/updateMessage', this.$t('component.finAgentStockMenu.addToWishlistSuccess'));
            } catch (error) {
                this.handleBackendException(error, this.$t('common.errors.generalError'));
                this.$store.commit('snackbar/updateType', 'error');
                this.$store.commit('snackbar/updateMessage', this.errorMessage);
            }
        },
        async handleClickRemoveWishList() {
            try {
                await this.finAgentService.removeFromWatchList(this.stockData.isin);
                this.$store.commit('finAgent/removeWishListIsin', this.stockData.isin);
                this.$store.commit('snackbar/updateType', 'success');
                this.$store.commit('snackbar/updateMessage', this.$t('component.finAgentStockMenu.removedWishlistSuccess'));
            } catch (error) {
                this.handleBackendException(error, this.$t('common.errors.generalError'));
                this.$store.commit('snackbar/updateType', 'error');
                this.$store.commit('snackbar/updateMessage', this.errorMessage);
            }
        },
    },
}
